import Modal from 'LuxKit/Modal/Modal'
import { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { semiFormToObject } from 'lib/forms/formUtils'
import useQueryParams from 'hooks/useQueryParams'
import Group from 'components/utils/Group'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import CruiseCruiseTypeFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseCruiseTypeFilter'
import type {
  CruiseSearchCruiseTypeChipProps,
} from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSearchMobileChips/CruiseSearchCruiseTypeChip'

function ModalCruiseSearchCruiseTypeChip(props: CruiseSearchCruiseTypeChipProps) {
  const {
    facets,
    filterForm,
    setFilterForm,
    fetching,
    filters,
    drawerMode,
  } = props
  const intl = useIntl()
  const modalElement = useModalElementContext<number>()
  const containerRef = useRef<HTMLDivElement>(null)
  const queryParams = useQueryParams()

  const countSelected = useMemo(() => {
    return queryParams.getAll('cruiseTypes').length
  }, [queryParams])

  const closeModal = useCallback(() => {
    modalElement.resolve(countSelected)
  }, [countSelected, modalElement])

  const updateForm = useCallback(() => {
    const formValues = semiFormToObject<Record<string, any>>(containerRef.current!)
    if (formValues && Object.keys(formValues).length > 0) {
      setFilterForm({
        ...filterForm,
        cruiseTypes: formValues.cruiseTypes,
      })
    } else {
      setFilterForm({
        ...filterForm,
        cruiseTypes: undefined,
      })
    }
  }, [filterForm, setFilterForm])

  const resetFilter = useCallback(() => {
    setFilterForm({
      ...filterForm,
      cruiseTypes: undefined,
    })
    modalElement.resolve(0)
  }, [filterForm, setFilterForm, modalElement])

  const modalTitle = intl.formatMessage({
    id: 'CDh4KwRX-05w9dpDkV65',
    defaultMessage: 'Cruise Type',
    description: 'Modal title for cruise type filter in the cruise offer or booking section',
  })
  const applyLabel = intl.formatMessage({
    id: '-CaRpOppo8I-EDtZ5zF1',
    defaultMessage: 'Apply',
    description: 'Button text to apply cruise type filter selections',
  })
  const resetLabel = intl.formatMessage(
    {
      id: 'ILp3o3CFyVKcvn9kSqMT',
      defaultMessage: '{count, plural, =0 {Reset all} one {Reset all (#)} other {Reset all (#)}}',
      description: 'Button text to reset cruise type filters with optional count of active filters',
    },
    { count: countSelected },
  )

  return <Modal
    mode={drawerMode ? 'drawer' : 'default'}
    ref={containerRef}
    title={modalTitle}
    primaryActionText={applyLabel}
    onPrimaryActionClick={closeModal}
    secondaryActionText={resetLabel}
    onSecondaryActionClick={resetFilter}
  >
    {fetching && <Group direction="vertical" gap={8}>
      <CheckboxInput size="large"><TextLoadingBox width="100%" /></CheckboxInput>
      <CheckboxInput size="large"><TextLoadingBox width="100%" /></CheckboxInput>
    </Group>}
    {!fetching && <CruiseCruiseTypeFilter
      cruiseTypes={filters.cruiseTypes}
      onChange={updateForm}
      facets={facets}
    />}
  </Modal>
}

export default ModalCruiseSearchCruiseTypeChip
